import {adjustSelectedIndex} from './Utils'
import {action, computed, computedFn, observable} from '@api'
import {DefaultNodeType, DockLocation, DropInfo, IDraggable, IDropTarget, IJsonBorderNode, ILayoutMetrics, Model, Orientation, Rect, SplitterNode, TabNode, TabSetNode} from '../'
import {FlexLayoutNode} from './FlexLayoutNode'
import {AttributeDefinitions} from '../AttributeDefinitions'
import {Attribute} from '../Attribute'

export class BorderNode extends FlexLayoutNode<DefaultNodeType & { config: any, show: boolean, size: number }, IJsonBorderNode> implements IDropTarget {
  static readonly TYPE = 'border'
  @action onDoubleClick = (e: React.MouseEvent) => {
    _.merge(this.attributes, {config: {collapsed: !this.isCollapsed}})
    this.model._changeListener()
  }

  set isCollapsed(v) {
    _.merge(this.attributes, {config: {collapsed: v}})
  }
  @computed get isCollapsed() { return this.config?.collapsed === true}

  /** @hidden @internal */
  static _fromJson(json: any, model: Model) {
    const location = DockLocation.getByName(json.location)
    const border = new BorderNode(location, json, model)
    if (json.children) {
      border._children = json.children.map((jsonChild: any) => {
        const child = TabNode._fromJson(jsonChild, model)
        child._setParent(border)
        return child
      })
    }

    return border
  }

  /** @hidden @internal */
  private static _attributeDefinitions: AttributeDefinitions = BorderNode._createAttributeDefinitions()

  /** @hidden @internal */
  private static _createAttributeDefinitions(): AttributeDefinitions {
    const attributeDefinitions = new AttributeDefinitions()
    attributeDefinitions.add('type', BorderNode.TYPE, true).setType(Attribute.STRING).setFixed()

    attributeDefinitions.add('selected', -1).setType(Attribute.NUMBER)
    attributeDefinitions.add('show', true).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('config', undefined).setType('any')

    attributeDefinitions.addInherited('barSize', 'borderBarSize').setType(Attribute.NUMBER)
    attributeDefinitions.addInherited('enableDrop', 'borderEnableDrop').setType(Attribute.BOOLEAN)
    attributeDefinitions.addInherited('className', 'borderClassName').setType(Attribute.STRING)
    attributeDefinitions.addInherited('autoSelectTabWhenOpen', 'borderAutoSelectTabWhenOpen').setType(Attribute.BOOLEAN)
    attributeDefinitions.addInherited('autoSelectTabWhenClosed', 'borderAutoSelectTabWhenClosed').setType(Attribute.BOOLEAN)
    attributeDefinitions.addInherited('size', 'borderSize').setType(Attribute.NUMBER)
    attributeDefinitions.addInherited('minSize', 'borderMinSize').setType(Attribute.NUMBER)
    return attributeDefinitions
  }

  /** @hidden @internal */
  @observable private _contentRect?: Rect
  /** @hidden @internal */
  @observable private _tabHeaderRect?: Rect
  /** @hidden @internal */
  @observable private _location: DockLocation
  /** @hidden @internal */
  @observable private _drawChildren: FlexLayoutNode[]
  /** @hidden @internal */
  @observable private _adjustedSize: number = 0
  /** @hidden @internal */
  @observable private _calculatedBorderBarSize: number = 0

  /** @hidden @internal */
  constructor(location: DockLocation, json: any, model: Model) {
    super(model)

    this._location = location
    this._drawChildren = []
    this._attributes.id = `border_${location.name}`
    BorderNode._attributeDefinitions.fromJson(json, this._attributes)
    model._addNode(this)
  }

  @computed get location() {
    return this._location
  }

  @computed get tabHeaderRect() {
    return this._tabHeaderRect
  }

  get rect() {
    return this._tabHeaderRect!
  }

  @computed get contentRect() {
    return this._contentRect
  }

  @computed get isEnableDrop() {
    return this._getAttr('enableDrop') as boolean
  }

  isAutoSelectTab = computedFn((whenOpen?: boolean) => {
    if (whenOpen == null) {
      whenOpen = this.selected !== -1
    }
    if (whenOpen) {
      return this._getAttr('autoSelectTabWhenOpen') as boolean
    } else {
      return this._getAttr('autoSelectTabWhenClosed') as boolean
    }
  })

  @computed get className() {
    return this._getAttr('className') as string | undefined
  }

  /** @hidden @internal */
  @action calcBorderBarSize = (metrics: ILayoutMetrics) => {
    const barSize = this._getAttr('barSize') as number
    if (barSize !== 0) {
      // its defined
      this._calculatedBorderBarSize = barSize
    } else {
      this._calculatedBorderBarSize = metrics.borderBarSize
    }
  }

  @computed get borderBarSize() {
    const {flexLayout: {hideTabSets}} = this
    if (hideTabSets) return 0

    return this._calculatedBorderBarSize
  }

  @computed get size() {
    const {flexLayout: {hideTabSets}} = this
    return hideTabSets ? 0 : this._getAttr('size') as number
  }

  @computed get minSize() {
    return this._getAttr('minSize') as number
  }

  @computed get selected(): number {
    return this._attributes.selected as number
  }

  @computed get selectedNode(): FlexLayoutNode | undefined {
    if (this.selected !== -1) {
      return this._children[this.selected]
    }
    return undefined
  }

  @computed get orientation() {
    return this._location.orientation
  }

  /**
   * Returns the config attribute that can be used to store node specific data that
   * WILL be saved to the json. The config attribute should be changed via the action Actions.updateNodeAttributes rather
   * than directly, for example:
   * this.state.model.doAction(
   *   FlexLayout.Actions.updateNodeAttributes(node.id, {config:myConfigObject}));
   */
  get config() {
    return this._attributes.config
  }

  @computed get isShowing() {
    return this._attributes.show as boolean
  }

  /** @hidden @internal */
  _setSelected = (index: number) => {
    this._attributes.selected = index
  }

  /** @hidden @internal */
  _setSize = (pos: number) => {
    this._attributes.size = pos
  }

  /** @hidden @internal */
  _updateAttrs = (json: any) => {
    BorderNode._attributeDefinitions.update(json, this._attributes)
  }

  /** @hidden @internal */
  get drawChildren() {
    return this._drawChildren
  }

  /** @hidden @internal */
  _setAdjustedSize = (size: number) => {
    this._adjustedSize = size
  }

  /** @hidden @internal */
  _getAdjustedSize() {
    return this._adjustedSize
  }

  /** @hidden @internal */
  @action _layoutBorderOuter = (outer: Rect, metrics: ILayoutMetrics) => {
    this.calcBorderBarSize(metrics)
    const split1 = this._location.split(outer, this.borderBarSize) // split border outer
    if (!this._tabHeaderRect || !this._tabHeaderRect.equals(split1.start)) {
      this._tabHeaderRect = split1.start
    }
    return split1.end
  }

  /** @hidden @internal */
  @action _layoutBorderInner = (inner: Rect, metrics: ILayoutMetrics) => {
    this._drawChildren = []
    const location = this._location

    const split1 = location.split(inner, this._adjustedSize + this._model.splitterSize) // split off tab contents
    const split2 = location.reflect().split(split1.start, this._model.splitterSize) // split contents into content and splitter

    this._contentRect = split2.end

    this._children.forEach((child, i) => {
      child._layout(this._contentRect!, metrics)
      this._drawChildren.push(child)
    })

    if (this.selected === -1) {
      return inner
    } else {
      const newSplitter = new SplitterNode(this._model)
      newSplitter._setParent(this)
      newSplitter._setRect(split2.start)
      this._drawChildren.push(newSplitter)

      return split1.end
    }
  }

  /** @hidden @internal */
  _remove(node: TabNode) {
    const removedIndex = this._removeChild(node)
    if (this.selected !== -1) {
      adjustSelectedIndex(this, removedIndex)
    }
  }

  /** @hidden @internal */
  canDrop(dragNode: FlexLayoutNode & IDraggable, x: number, y: number): DropInfo | undefined {
    if (dragNode.type !== TabNode.TYPE) {
      return undefined
    }

    let dropInfo
    const dockLocation = DockLocation.CENTER

    if (this._tabHeaderRect!.contains(x, y)) {
      if (this._location._orientation === Orientation.VERT) {
        if (this._children.length > 0) {
          let child = this._children[0]
          let childRect = (child as TabNode).tabRect!
          const childY = childRect.y

          const childHeight = childRect.height

          let pos = this._tabHeaderRect!.x
          let childCenter = 0
          for (let i = 0; i < this._children.length; i++) {
            child = this._children[i]
            childRect = (child as TabNode).tabRect!
            childCenter = childRect.x + childRect.width / 2
            if (x >= pos && x < childCenter) {
              const outlineRect = new Rect(childRect.x - 2, childY, 3, childHeight)
              dropInfo = new DropInfo(this, outlineRect, dockLocation, i, 'flexlayout__outline_rect')
              break
            }
            pos = childCenter
          }
          if (dropInfo == null) {
            const outlineRect = new Rect(childRect.right - 2, childY, 3, childHeight)
            dropInfo = new DropInfo(this, outlineRect, dockLocation, this._children.length, 'flexlayout__outline_rect')
          }
        } else {
          const outlineRect = new Rect(this._tabHeaderRect!.x + 1, this._tabHeaderRect!.y + 2, 3, 18)
          dropInfo = new DropInfo(this, outlineRect, dockLocation, 0, 'flexlayout__outline_rect')
        }
      } else {
        if (this._children.length > 0) {
          let child = this._children[0]
          let childRect = (child as TabNode).tabRect!
          const childX = childRect.x
          const childWidth = childRect.width

          let pos = this._tabHeaderRect!.y
          let childCenter = 0
          for (let i = 0; i < this._children.length; i++) {
            child = this._children[i]
            childRect = (child as TabNode).tabRect!
            childCenter = childRect.y + childRect.height / 2
            if (y >= pos && y < childCenter) {
              const outlineRect = new Rect(childX, childRect.y - 2, childWidth, 3)
              dropInfo = new DropInfo(this, outlineRect, dockLocation, i, 'flexlayout__outline_rect')
              break
            }
            pos = childCenter
          }
          if (dropInfo == null) {
            const outlineRect = new Rect(childX, childRect.bottom - 2, childWidth, 3)
            dropInfo = new DropInfo(this, outlineRect, dockLocation, this._children.length, 'flexlayout__outline_rect')
          }
        } else {
          const outlineRect = new Rect(this._tabHeaderRect!.x + 2, this._tabHeaderRect!.y + 1, 18, 3)
          dropInfo = new DropInfo(this, outlineRect, dockLocation, 0, 'flexlayout__outline_rect')
        }
      }
      if (!dragNode._canDockInto(dragNode, dropInfo)) {
        return undefined
      }
    } else if (this.selected !== -1 && this._contentRect!.contains(x, y)) {
      const outlineRect = this._contentRect
      dropInfo = new DropInfo(this, outlineRect!, dockLocation, -1, 'flexlayout__outline_rect')
      if (!dragNode._canDockInto(dragNode, dropInfo)) {
        return undefined
      }
    }

    return dropInfo
  }

  /** @hidden @internal */
  @action drop = (dragNode: FlexLayoutNode & IDraggable, location: DockLocation, index: number, select?: boolean): void => {
    let fromIndex = 0
    const dragParent = dragNode.parent as BorderNode | TabSetNode
    if (dragParent !== undefined) {
      fromIndex = dragParent._removeChild(dragNode)
      adjustSelectedIndex(dragParent, fromIndex)
    }

    // if dropping a tab back to same tabset and moving to forward position then reduce insertion index
    if (dragNode.type === TabNode.TYPE && dragParent === this && fromIndex < index && index > 0) {
      index--
    }

    // simple_bundled dock to existing tabset
    let insertPos = index
    if (insertPos === -1) {
      insertPos = this._children.length
    }

    if (dragNode.type === TabNode.TYPE) {
      this.addChild(dragNode, insertPos)
    }

    if (select || (select !== false && this.isAutoSelectTab)) {
      this._setSelected(insertPos)
    }

    this._model.tidy()
  }

  /** @hidden @internal */
  @computed get asJson() {
    const json: any = {}
    BorderNode._attributeDefinitions.toJson(json, this._attributes)
    json.location = this._location.name
    json.children = this._children.map((child) => (child as TabNode).asJson)
    return json
  }

  /** @hidden @internal */
  _getSplitterBounds = computedFn((splitter: SplitterNode, useMinSize: boolean = false) => {
    const pBounds = [0, 0]
    const minSize = useMinSize ? this.minSize : 0
    const outerRect = this._model.outerInnerRects.outer
    const innerRect = this._model.outerInnerRects.inner
    if (this._location === DockLocation.TOP) {
      pBounds[0] = outerRect.y + minSize
      pBounds[1] = innerRect.bottom - splitter.height
    } else if (this._location === DockLocation.LEFT) {
      pBounds[0] = outerRect.x + minSize
      pBounds[1] = innerRect.right - splitter.width
    } else if (this._location === DockLocation.BOTTOM) {
      pBounds[0] = innerRect.y
      pBounds[1] = outerRect.bottom - splitter.height - minSize
    } else if (this._location === DockLocation.RIGHT) {
      pBounds[0] = innerRect.x
      pBounds[1] = outerRect.right - splitter.width - minSize
    }
    return pBounds
  })


  /** @hidden @internal */
  _calculateSplit(splitter: SplitterNode, splitterPos: number) {
    const pBounds = this._getSplitterBounds(splitter)
    if (this._location === DockLocation.BOTTOM || this._location === DockLocation.RIGHT) {
      return Math.max(0, pBounds[1] - splitterPos)
    } else {
      return Math.max(0, splitterPos - pBounds[0])
    }
  }

  /** @hidden @internal */
  _getAttributeDefinitions() {
    return BorderNode._attributeDefinitions
  }

  /** @hidden @internal */
  static getAttributeDefinitions() {
    return BorderNode._attributeDefinitions
  }
}



// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexlayout-react/src/model/BorderNode.ts